import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { FileText, Clock, Loader2, Upload } from 'lucide-react';


export default function ExtractionProgress({ progressData, setProgressData, onComplete }) {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    const startProcessing = async () => {
      // Call the actual backend API
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

        
        setProgressData(prev => ({
          ...prev,
          currentFile: 'Uploading files...',
          filesProcessed: 0,
          totalFiles: progressData.totalFiles || 1,
          progress: 10
        }));

        const response = await fetch(`${BACKEND_URL}/api/extract/upload`, {
          method: 'POST',
          body: progressData.formData
        });

        if (!response.ok) {
          throw new Error(`Upload failed: ${response.statusText}`);
        }

        const data = await response.json();
        
        setProgressData(prev => ({
          ...prev,
          progress: 100,
          filesProcessed: progressData.totalFiles
        }));

        // Transform backend response to match expected format
        const transformedData = data.documents.map(doc => ({
          fileName: doc.document_name,
          results: doc.results
        }));

        setTimeout(() => {
          onComplete(transformedData);
        }, 500);

      } catch (error) {
        console.error('Extraction failed:', error);
        alert(`Extraction failed: ${error.message}`);
      }
    };

    startProcessing();
  }, []);

  useEffect(() => {
    const timeInterval = setInterval(() => {
      if (progressData.startTime) {
        const elapsed = Math.floor((Date.now() - progressData.startTime) / 1000);
        setElapsedSeconds(elapsed);
      }
    }, 1000);

    return () => clearInterval(timeInterval);
  }, [progressData.startTime]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-6">
      {/* Progress Overview Card */}
      <Card className="shadow-lg border-border/50">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl font-bold">Extraction in Progress</CardTitle>
              <CardDescription className="text-base mt-1">
                Processing uploaded PDF files
              </CardDescription>
            </div>
            <Badge variant="outline" className="px-3 py-2 text-sm border-accent text-accent">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Active
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Progress Bar */}
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-foreground">
                {progressData.filesProcessed} of {progressData.totalFiles} files processed
              </span>
              <span className="font-bold text-primary">
                {Math.round(progressData.progress)}%
              </span>
            </div>
            <Progress value={progressData.progress} className="h-3" />
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
            <div className="flex items-center gap-3 p-4 rounded-lg bg-primary/5 border border-primary/10">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Current Status</p>
                <p className="text-sm font-semibold text-foreground truncate max-w-[200px]">
                  {progressData.currentFile || 'Initializing...'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-4 rounded-lg bg-accent/5 border border-accent/10">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10">
                <Upload className="h-5 w-5 text-accent" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Completed</p>
                <p className="text-sm font-semibold text-foreground">
                  {progressData.filesProcessed} files
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-4 rounded-lg bg-secondary/5 border border-secondary/10">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary/10">
                <Clock className="h-5 w-5 text-secondary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Elapsed Time</p>
                <p className="text-sm font-semibold text-foreground">
                  {formatTime(elapsedSeconds)}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
